// src/account-setup.schema.ts
import { z as z2 } from "zod";

// src/utils.ts
import { z } from "zod";
function createUpdateSchema(schema) {
  const shape = schema.shape;
  const optionalShape = Object.keys(shape).reduce(
    (acc, key) => {
      acc[key] = shape[key].optional();
      return acc;
    },
    {}
  );
  return z.object(optionalShape);
}
function omitSensitive(schema, keys) {
  const omitKeys = keys.reduce(
    (acc, key) => {
      acc[key] = true;
      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {}
  );
  return schema.omit(omitKeys);
}
var hasScheme = (value) => /^[a-z][a-z0-9+.-]*:/i.test(value) && !/^[a-z][a-z0-9+.-]*:\d/i.test(value);
var companyWebsite = () => z.string().trim().transform((value) => value === "" || hasScheme(value) ? value : `https://${value}`).superRefine((value, ctx) => {
  if (value === "") return;
  let url;
  try {
    url = new URL(value);
  } catch {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Please enter a valid URL" });
    return;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Please enter a valid URL" });
  }
});

// src/account-setup.schema.ts
var IndustryEnum = z2.enum([
  "SOLICITORS",
  "ESTATE_AGENTS",
  "FINANCIAL_INSTITUTIONS",
  "ACCOUNTANCY",
  "TCSPS",
  "HIGH_VALUE_DEALERS",
  "GAMBLING_OPERATOR",
  "ART_MARKET",
  "CRYPTOASSET_EXCHANGE",
  "CUSTODIAN_WALLET"
]);
var AccountSetupSchema = z2.object({
  // User Information
  title: z2.string().min(1, "Title is required").max(20),
  forename: z2.string().min(1, "Forename is required").max(100),
  middleName: z2.string().max(100).optional(),
  surname: z2.string().min(1, "Surname is required").max(100),
  jobTitle: z2.string().min(1, "Job title is required").max(100),
  // Company Information
  industry: IndustryEnum,
  companyName: z2.string().min(1, "Company name is required").max(200),
  // UK Companies House registration numbers are exactly 8 characters
  // (8 digits, or a 2-letter prefix followed by 6 alphanumerics, e.g. SC123456).
  companyNumber: z2.string().trim().min(1, "Companies House registration number is required").regex(/^[A-Za-z0-9]{8}$/, "Enter a valid 8-character Companies House number").transform((value) => value.toUpperCase()),
  companyWebsite: companyWebsite().optional(),
  // Company Registered Address — the company's Companies House registered
  // office. Persisted as Company.addressLine1 etc.
  addressLine1: z2.string().min(1, "Address line 1 is required").max(200),
  addressLine2: z2.string().max(200).optional(),
  townCity: z2.string().min(1, "Town/City is required").max(100),
  county: z2.string().max(100).optional(),
  postcode: z2.string().min(1, "Postcode is required").max(20),
  country: z2.string().length(2, "Country must be a 2-letter ISO code"),
  // User's Personal Address — optional. Captured during account setup for
  // compliance records and account verification when provided. Stored on the
  // User row (User.addressLine1 etc.); the company's Companies House registered
  // office sits separately on Company.addressLine1 etc. (mandatory above). The
  // `personal*` prefix only lives in the DTO/form layer to disambiguate the two
  // addresses inside this flat payload.
  personalAddressLine1: z2.string().max(200).optional().or(z2.literal("")),
  personalAddressLine2: z2.string().max(200).optional(),
  personalTownCity: z2.string().max(100).optional().or(z2.literal("")),
  personalCounty: z2.string().max(100).optional(),
  personalPostcode: z2.string().max(20).optional().or(z2.literal("")),
  personalCountry: z2.string().length(2, "Personal country must be a 2-letter ISO code").optional().or(z2.literal("")),
  // Terms
  termsAccepted: z2.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions"
  })
});
var CorrectCompanyClaimSchema = AccountSetupSchema.pick({
  industry: true,
  companyName: true,
  companyWebsite: true,
  addressLine1: true,
  addressLine2: true,
  townCity: true,
  county: true,
  postcode: true,
  country: true
}).partial().extend({
  companyNumber: AccountSetupSchema.shape.companyNumber.or(z2.literal("")).optional()
});

// src/account-status.ts
import { z as z3 } from "zod";
var ACCOUNT_STATUSES = ["PENDING", "APPROVED", "REJECTED", "DISABLED"];
var ACCOUNT_REFUSAL_CODES = [
  "ACCOUNT_DISABLED",
  "ACCOUNT_REJECTED",
  "ACCOUNT_PENDING_REVIEW",
  "ACCOUNT_NOT_FOUND"
];
var reason = z3.string().trim().min(1, "A reason is required").max(500, "A reason must be 500 characters or fewer");
var RejectUserSchema = z3.object({ reason });
var DisableUserSchema = z3.object({ reason });
var COMPANY_OUTCOME_KINDS = ["attach", "create", "none", "ambiguous"];
var ApproveUserSchema = z3.object({
  expectedOutcome: z3.enum(COMPANY_OUTCOME_KINDS).optional(),
  /**
   * The firm the modal named, when the outcome was `attach`.
   *
   * The kind alone is not enough. If the matched firm cleared its Companies
   * House number and another firm claimed it while the modal was open, the
   * outcome is still `attach` — but onto a different company than the one whose
   * name and balance the administrator was shown. Pinning the id means the
   * approval fails rather than attaching someone to a firm nobody reviewed.
   */
  expectedCompanyId: z3.string().uuid().optional()
});
var ReassignCompanySchema = z3.object({
  companyId: z3.string().uuid(),
  expectedCompanyId: z3.string().uuid().nullable(),
  reason
});

// src/activity-log.schema.ts
import { z as z5 } from "zod";

// src/date-range.schema.ts
import { z as z4 } from "zod";
var DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
var InclusiveEndDateSchema = z4.preprocess((value) => {
  if (typeof value === "string" && DATE_ONLY_PATTERN.test(value)) {
    return /* @__PURE__ */ new Date(`${value}T23:59:59.999Z`);
  }
  return value;
}, z4.coerce.date());

// src/activity-log.schema.ts
var ActivityLogItemSchema = z5.object({
  id: z5.string().uuid(),
  timestamp: z5.coerce.date(),
  actorId: z5.string(),
  actorType: z5.string(),
  actorEmail: z5.string().nullable(),
  action: z5.string(),
  category: z5.string(),
  status: z5.string(),
  targetType: z5.string().nullable(),
  targetId: z5.string().nullable(),
  summary: z5.string(),
  details: z5.unknown()
});
var ActivityLogPageSchema = z5.object({
  items: z5.array(ActivityLogItemSchema),
  total: z5.number().int().nonnegative(),
  page: z5.number().int().min(1),
  pageSize: z5.number().int().min(1),
  totalPages: z5.number().int().nonnegative()
});
var ACTIVITY_SORT_COLUMNS = ["timestamp", "action", "category", "status"];
var ActivityLogQuerySchema = z5.object({
  /** Matches actorId exactly, or actorEmail case-insensitively. */
  actor: z5.string().trim().min(1).max(320).optional(),
  action: z5.string().trim().min(1).max(100).optional(),
  category: z5.string().trim().min(1).max(50).optional(),
  status: z5.enum(["success", "failure"]).optional(),
  targetId: z5.string().trim().min(1).max(100).optional(),
  from: z5.coerce.date().optional(),
  /**
   * End of the range, inclusive of the whole day named. A bare date would
   * otherwise coerce to midnight and exclude that day entirely.
   */
  to: InclusiveEndDateSchema.optional(),
  // Same names and bounds as the admin user and assessment lists, so one
  // Pagination control drives all three without a translation layer.
  page: z5.coerce.number().int().min(1).default(1),
  pageSize: z5.coerce.number().int().min(1).max(100).default(20),
  // No default, unlike the user list: absent means the log's own newest-first
  // order, which is not expressible as a column plus a direction — it also
  // orders the `id` tiebreaker descending, so a page boundary inside a group of
  // same-instant rows stays put. Defaulting this to `timestamp` would quietly
  // change that order for every caller that never asked to sort.
  sortBy: z5.enum(ACTIVITY_SORT_COLUMNS).optional(),
  // Optional rather than defaulted, unlike the user list: a direction with no
  // column to apply it to means nothing here, so the pair travels together and
  // the descending fallback lives with the ordering it belongs to.
  sortOrder: z5.enum(["asc", "desc"]).optional()
});

// src/admin.schema.ts
import { z as z6 } from "zod";
var AdminOverviewSchema = z6.object({
  users: z6.number().int().nonnegative(),
  companies: z6.number().int().nonnegative(),
  assessments: z6.number().int().nonnegative()
});

// src/admin-assessment.schema.ts
import { z as z8 } from "zod";

// src/csv-list.ts
import { z as z7 } from "zod";
function toList(value) {
  if (value === void 0 || value === null) return void 0;
  const parts = Array.isArray(value) ? value : [value];
  const values = [];
  for (const part of parts) {
    if (typeof part !== "string") return value;
    for (const piece of part.split(",")) {
      const trimmed = piece.trim();
      if (trimmed && !values.includes(trimmed)) values.push(trimmed);
    }
  }
  return values.length > 0 ? values : void 0;
}
function csvList(item) {
  return z7.preprocess(toList, z7.array(item).min(1).optional());
}

// src/admin-assessment.schema.ts
var AdminAssessmentRiskLevelSchema = z8.enum(["LOW", "MEDIUM", "HIGH"]);
var ASSESSMENT_RISK_LEVELS = AdminAssessmentRiskLevelSchema.options;
var AdminAssessmentStatusSchema = z8.enum(["IN_PROGRESS", "COMPLETED", "DELETED"]);
var ASSESSMENT_STATUSES = AdminAssessmentStatusSchema.options;
var AdminAssessmentReportStateSchema = z8.enum([
  "available",
  "expired",
  "purged",
  "never_generated"
]);
var AdminAssessmentListItemSchema = z8.object({
  id: z8.string().uuid(),
  optimalKycRef: z8.string(),
  clientRef: z8.string(),
  /**
   * Empty on a purged row, because the retention policy wipes it. Read
   * alongside `purged` rather than treating an empty string as missing data.
   */
  clientName: z8.string(),
  /** Whether the retention policy has wiped this row's PII. */
  purged: z8.boolean(),
  riskLevel: AdminAssessmentRiskLevelSchema,
  riskScore: z8.number().int().nullable(),
  status: AdminAssessmentStatusSchema,
  ownerId: z8.string().uuid(),
  ownerEmail: z8.string(),
  ownerCompany: z8.string().nullable(),
  createdAt: z8.coerce.date()
});
var AdminAssessmentPageSchema = z8.object({
  items: z8.array(AdminAssessmentListItemSchema),
  total: z8.number().int().nonnegative(),
  page: z8.number().int().min(1),
  pageSize: z8.number().int().min(1),
  totalPages: z8.number().int().nonnegative()
});
var AdminAssessmentDetailSchema = AdminAssessmentListItemSchema.extend({
  clientEmail: z8.string().nullable(),
  ownerName: z8.string().nullable(),
  /** Screening checks that were enabled for this assessment, e.g. 'UK', 'PEP'. */
  enabledChecks: z8.array(z8.string()),
  reportState: AdminAssessmentReportStateSchema,
  /**
   * The generated report as stored markdown, or null when there is none to
   * show — purged, expired, or never generated. `reportState` says which.
   *
   * Carried on the detail only, never the list: it is the report itself rather
   * than metadata about it, and sending one per row would be a large payload
   * nothing on that screen reads.
   */
  report: z8.string().nullable(),
  dateReportGenerated: z8.coerce.date().nullable(),
  reportExpiresAt: z8.coerce.date().nullable(),
  purgedAt: z8.coerce.date().nullable(),
  dateDeleted: z8.coerce.date().nullable(),
  updatedAt: z8.coerce.date(),
  screeningJobStatus: z8.string(),
  refreshCount: z8.number().int().nonnegative(),
  isLegalMatter: z8.boolean(),
  /**
   * Whether this case is a demo fixture: assessments matching its client
   * reference and name are served this stored report instead of a live
   * screening.
   *
   * Read-only, deliberately. There is no endpoint to set it — fixtures come
   * only from the seeder, whose subjects and findings are fabricated. Marking
   * an arbitrary case would publish that customer's reference, name and report
   * to every user through the client-reference picker.
   */
  isDemoFixture: z8.boolean()
});
var ASSESSMENT_SORT_COLUMNS = ["createdAt", "riskLevel", "status"];
var AdminAssessmentQuerySchema = z8.object({
  /** Several levels may be named at once; an assessment at any of them is included. */
  riskLevel: csvList(AdminAssessmentRiskLevelSchema),
  /**
   * Absent means "everything except DELETED". Deleted assessments are excluded
   * by default so the list agrees with the overview count, but remain reachable
   * here — a user deleting an assessment is exactly what an administrator might
   * be looking for.
   */
  status: csvList(AdminAssessmentStatusSchema),
  /** Matches the owner's user id exactly, or their email case-insensitively. */
  owner: z8.string().trim().min(1).max(320).optional(),
  /**
   * Narrows to assessments whose owner belongs to this company. How the risk
   * roll-up drills through: a firm on that screen links here.
   *
   * An id matching no company returns an empty page rather than an error —
   * filtering to nothing is a valid answer for a filter.
   */
  company: z8.string().uuid().optional(),
  /** Substring match, case-insensitive, over optimalKycRef, clientRef and clientName. */
  search: z8.string().trim().min(1).max(200).optional(),
  from: z8.coerce.date().optional(),
  /**
   * End of the range, inclusive of the whole day named. A bare date would
   * otherwise coerce to midnight and exclude that day entirely.
   */
  to: InclusiveEndDateSchema.optional(),
  // Same names and bounds as the admin user list, so one Pagination control
  // drives both without a translation layer between them.
  page: z8.coerce.number().int().min(1).default(1),
  pageSize: z8.coerce.number().int().min(1).max(100).default(20),
  // No default: absent means the list's own newest-first order, which is not
  // expressible as a column plus a direction — it also orders the `id`
  // tiebreaker descending, so a page boundary inside a group of same-instant
  // rows stays put. Defaulting this to `createdAt` would quietly change that
  // order for every caller that never asked to sort.
  sortBy: z8.enum(ASSESSMENT_SORT_COLUMNS).optional(),
  // Optional rather than defaulted: a direction with no column to apply it to
  // means nothing here, so the pair travels together and the descending
  // fallback lives with the ordering it belongs to.
  sortOrder: z8.enum(["asc", "desc"]).optional()
});

// src/admin-company-credit.schema.ts
import { z as z9 } from "zod";
var MAX_CREDIT_PENCE = 1e7;
var CreditableCompanySchema = z9.object({
  id: z9.string().uuid(),
  name: z9.string(),
  /** Current pool, in pence. */
  balancePence: z9.number().int(),
  userCount: z9.number().int().nonnegative()
});
var CreditCompanySchema = z9.object({
  amountPence: z9.number().int("Amount must be a whole number of pence").positive("Amount must be greater than zero").max(MAX_CREDIT_PENCE, "Amount exceeds the maximum single credit"),
  /**
   * Why the credit was given.
   *
   * Required, because a credit with no stated reason is exactly the entry
   * nobody can account for later — which is the situation the audit trail
   * exists to prevent.
   */
  reason: z9.string().trim().min(3, "Give a reason").max(500)
});
var CreditCompanyResultSchema = z9.object({
  companyId: z9.string().uuid(),
  companyName: z9.string(),
  amountPence: z9.number().int(),
  balancePence: z9.number().int(),
  transactionId: z9.string().uuid()
});

// src/admin-company-risk.schema.ts
import { z as z10 } from "zod";
var NO_COMPANY_ID = "no-company";
var CompanyRiskRowSchema = z10.object({
  /** Company id, or NO_COMPANY_ID for owners belonging to no company. */
  companyId: z10.string(),
  companyName: z10.string(),
  /**
   * Companies House number, where the firm has one.
   *
   * Null for firms created before a number was required at setup, and for the
   * "No company" catch-all row. Most existing firms have none.
   */
  companyNumber: z10.string().nullable(),
  /**
   * Users belonging to the firm.
   *
   * Every one of them, not only those with an assessment — the query counts
   * distinct users over a LEFT JOIN, so a member who has never screened
   * anybody still counts. An earlier version of this comment claimed the
   * narrower reading; the SQL never did that.
   */
  userCount: z10.number().int().nonnegative(),
  low: z10.number().int().nonnegative(),
  medium: z10.number().int().nonnegative(),
  high: z10.number().int().nonnegative(),
  total: z10.number().int().nonnegative(),
  /** Highest riskScore seen, or null when no counted assessment carries one. */
  topScore: z10.number().int().nullable()
});
var CompanyRiskReportSchema = z10.object({
  rows: z10.array(CompanyRiskRowSchema),
  /**
   * Assessments counted across every row. Lets a caller check the roll-up
   * against the platform total rather than trusting it.
   */
  totalAssessments: z10.number().int().nonnegative()
});

// src/admin-transaction.schema.ts
import { z as z11 } from "zod";
var TRANSACTION_SOURCES = ["balance", "payment"];
var AdminTransactionSchema = z11.object({
  id: z11.string().uuid(),
  source: z11.enum(TRANSACTION_SOURCES),
  occurredAt: z11.coerce.date(),
  userId: z11.string().uuid(),
  userEmail: z11.string(),
  companyId: z11.string().uuid().nullable(),
  companyName: z11.string().nullable(),
  /** Signed: negative is money leaving the account. */
  amountPence: z11.number().int(),
  type: z11.string(),
  status: z11.string().nullable(),
  description: z11.string(),
  reference: z11.string().nullable()
});
var AdminTransactionPageSchema = z11.object({
  items: z11.array(AdminTransactionSchema),
  total: z11.number().int().nonnegative(),
  page: z11.number().int().min(1),
  pageSize: z11.number().int().min(1),
  totalPages: z11.number().int().nonnegative(),
  /**
   * Net balance movement across every matching row, not just this page.
   *
   * Counts balance rows only. A successful top-up appears twice — once as the
   * Stripe charge and once as the credit it produced — so adding both sources
   * would double every top-up on the platform. Balance rows are the ledger;
   * payments are the charge that caused one. Pending and failed payments moved
   * no money at all, which is a second reason not to sum them.
   *
   * Summing only the visible page would give a figure that changed as you paged
   * through, which reads as a bug.
   */
  netPence: z11.number().int()
});
var TRANSACTION_SORT_COLUMNS = ["occurredAt", "amountPence", "type"];
var AdminTransactionQuerySchema = z11.object({
  /** Matches the user's email, case-insensitively and partially. */
  user: z11.string().trim().min(1).max(320).optional(),
  companyId: z11.string().uuid().optional(),
  source: z11.enum(TRANSACTION_SOURCES).optional(),
  /** A TransactionType for balance rows, or a PaymentStatus for payments. */
  type: csvList(z11.string().trim().min(1).max(50)),
  from: z11.coerce.date().optional(),
  /**
   * End of the range, inclusive of the whole day named. A bare date would
   * otherwise coerce to midnight and exclude that day's transactions from the
   * rows, the total and the net movement.
   */
  to: InclusiveEndDateSchema.optional(),
  page: z11.coerce.number().int().min(1).default(1),
  pageSize: z11.coerce.number().int().min(1).max(100).default(20),
  // No default, matching the activity list: absent means the order this list
  // has always returned, which is not expressible as a column plus a direction
  // — its tiebreaker runs descending too. Defaulting to `occurredAt` would
  // quietly change the order for every caller that never asked to sort.
  sortBy: z11.enum(TRANSACTION_SORT_COLUMNS).optional(),
  // Optional rather than defaulted: a direction with no column to apply it to
  // means nothing, so the pair travels together.
  sortOrder: z11.enum(["asc", "desc"]).optional()
});

// src/admin-user.schema.ts
import { z as z12 } from "zod";
var ADMIN_USER_SORT_COLUMNS = [
  "email",
  "name",
  "company",
  "role",
  "createdAt",
  "balance"
];
var ADMIN_USER_STATES = [
  "active",
  "pending_review",
  "unverified",
  "setup_incomplete",
  "rejected",
  "disabled",
  "deleted"
];
var AdminUserListQuerySchema = z12.object({
  /** Matched case-insensitively against email, forename, surname and company name. */
  search: z12.string().trim().min(1).max(320).optional(),
  /** Several roles may be named at once; a user matching any of them is included. */
  role: csvList(z12.string().trim().min(1).max(50)),
  /** Several states may be named at once; a user in any of them is included. */
  state: csvList(z12.enum(ADMIN_USER_STATES)),
  /** Earliest `createdAt` to include, inclusive. */
  from: z12.coerce.date().optional(),
  /**
   * Latest `createdAt` to include, inclusive of the whole named day.
   *
   * See `InclusiveEndDateSchema`: a bare date covers to 23:59:59.999 UTC.
   */
  to: InclusiveEndDateSchema.optional(),
  page: z12.coerce.number().int().min(1).default(1),
  pageSize: z12.coerce.number().int().min(1).max(100).default(20),
  sortBy: z12.enum(ADMIN_USER_SORT_COLUMNS).default("createdAt"),
  sortOrder: z12.enum(["asc", "desc"]).default("desc")
});
var AdminUserListItemSchema = z12.object({
  id: z12.string().uuid(),
  email: z12.string(),
  fullName: z12.string().nullable(),
  jobTitle: z12.string().nullable(),
  companyName: z12.string().nullable(),
  roleName: z12.string(),
  emailVerified: z12.boolean(),
  accountSetupComplete: z12.boolean(),
  accountStatus: z12.enum(ACCOUNT_STATUSES),
  deletedAt: z12.coerce.date().nullable(),
  createdAt: z12.coerce.date(),
  balance: z12.string(),
  assessmentCount: z12.number().int().nonnegative()
});
var AdminUserListPageSchema = z12.object({
  items: z12.array(AdminUserListItemSchema),
  total: z12.number().int().nonnegative(),
  page: z12.number().int().min(1),
  pageSize: z12.number().int().min(1),
  totalPages: z12.number().int().nonnegative()
});
var AddressSchema = z12.object({
  addressLine1: z12.string().nullable(),
  addressLine2: z12.string().nullable(),
  townCity: z12.string().nullable(),
  county: z12.string().nullable(),
  postcode: z12.string().nullable(),
  country: z12.string()
});
var AdminUserDetailSchema = z12.object({
  id: z12.string().uuid(),
  email: z12.string(),
  applicant: z12.object({
    title: z12.string().nullable(),
    forename: z12.string().nullable(),
    middleName: z12.string().nullable(),
    surname: z12.string().nullable(),
    jobTitle: z12.string().nullable()
  }),
  address: AddressSchema,
  company: z12.object({
    id: z12.string().uuid(),
    name: z12.string(),
    companyNumber: z12.string().nullable(),
    industry: z12.string(),
    website: z12.string().nullable(),
    address: AddressSchema
  }).nullable(),
  /**
   * What an applicant claimed at setup, while they await a decision.
   *
   * Populated only before approval, and `company` is null for the same user —
   * the claim is not a firm on the platform yet. No `id` for that reason:
   * giving it one would invite treating it as a company that exists.
   */
  claimedCompany: z12.object({
    name: z12.string(),
    companyNumber: z12.string().nullable(),
    industry: z12.string(),
    website: z12.string().nullable(),
    address: AddressSchema
  }).nullable(),
  account: z12.object({
    roleName: z12.string(),
    emailVerified: z12.boolean(),
    emailVerifiedAt: z12.coerce.date().nullable(),
    accountSetupComplete: z12.boolean(),
    accountStatus: z12.enum(ACCOUNT_STATUSES),
    /**
     * Why the account is in that status, when an administrator had to say.
     *
     * Admin-facing only. The rejection email deliberately withholds it, so this
     * must not be echoed onto any user-facing route.
     */
    statusReason: z12.string().nullable(),
    dueDiligenceAlertFrequency: z12.string(),
    failedLoginAttempts: z12.number().int().nonnegative(),
    lockedUntil: z12.coerce.date().nullable(),
    deletedAt: z12.coerce.date().nullable(),
    deleteRequestedAt: z12.coerce.date().nullable(),
    createdAt: z12.coerce.date(),
    updatedAt: z12.coerce.date()
  }),
  summary: z12.object({
    assessmentCount: z12.number().int().nonnegative(),
    balance: z12.string(),
    lastLoginAt: z12.coerce.date().nullable()
  })
});

// src/auth.schema.ts
import { z as z13 } from "zod";
var LoginSchema = z13.object({
  email: z13.string().email("Invalid email address").describe("User email address"),
  password: z13.string().min(1, "Password is required").describe("User password")
});
var RegisterSchema = z13.object({
  email: z13.string().email("Invalid email address").describe("User email address"),
  password: z13.string().min(8, "Password must be at least 8 characters long").describe("User password (minimum 8 characters)")
});

// src/check-depth.schema.ts
import { z as z14 } from "zod";
var CheckDepthTierSchema = z14.enum(["basic", "standard", "enhanced"]);

// src/client-details.schema.ts
import { z as z15 } from "zod";
var ClientTypeSchema = z15.enum(["NEW", "EXISTING"]);
var EntityTypeSchema = z15.enum(["INDIVIDUAL", "ORGANISATION"]);
var ClientTitleSchema = z15.enum([
  // Civil
  "MR",
  "MRS",
  "MISS",
  "MS",
  "MX",
  // Professional/Academic
  "DR",
  "PROF",
  // Religious
  "REV",
  "FR",
  "SR",
  "BR",
  "BISHOP",
  "ARCHBISHOP",
  "CARDINAL",
  "CANON",
  "IMAM",
  "SHEIKH",
  "RABBI",
  "PASTOR",
  // Honour/Peerage/Political
  "SIR",
  "DAME",
  "LORD",
  "LADY",
  "BARON",
  "BARONESS",
  "THE_HON",
  "THE_RT_HON",
  // Judicial
  "JUDGE",
  "JUSTICE",
  "MAGISTRATE",
  // Military
  "CAPT",
  "CMDR",
  "COL",
  "MAJ",
  "LT",
  "SGT",
  "CPL",
  "WO",
  "ADM",
  "AIR_CDRE",
  "GP_CAPT",
  "FLT_LT",
  // Royal
  "HM",
  "HRH",
  // Formal
  "ESQ"
]);
var ClientTitleLabels = {
  MR: "Mr",
  MRS: "Mrs",
  MISS: "Miss",
  MS: "Ms",
  MX: "Mx",
  DR: "Dr",
  PROF: "Prof",
  REV: "Rev",
  FR: "Fr",
  SR: "Sr",
  BR: "Br",
  BISHOP: "Bishop",
  ARCHBISHOP: "Archbishop",
  CARDINAL: "Cardinal",
  CANON: "Canon",
  IMAM: "Imam",
  SHEIKH: "Sheikh",
  RABBI: "Rabbi",
  PASTOR: "Pastor",
  SIR: "Sir",
  DAME: "Dame",
  LORD: "Lord",
  LADY: "Lady",
  BARON: "Baron",
  BARONESS: "Baroness",
  THE_HON: "The Hon",
  THE_RT_HON: "The Rt Hon",
  JUDGE: "Judge",
  JUSTICE: "Justice",
  MAGISTRATE: "Magistrate",
  CAPT: "Capt",
  CMDR: "Cmdr",
  COL: "Col",
  MAJ: "Maj",
  LT: "Lt",
  SGT: "Sgt",
  CPL: "Cpl",
  WO: "WO",
  ADM: "Adm",
  AIR_CDRE: "Air Cdre",
  GP_CAPT: "Gp Capt",
  FLT_LT: "Flt Lt",
  HM: "HM",
  HRH: "HRH",
  ESQ: "Esq"
};
var MatterTypeSchema = z15.enum([
  "CONVEYANCING_RESIDENTIAL",
  "CONVEYANCING_COMMERCIAL",
  "PROBATE_AND_WILLS",
  "FAMILY_LAW",
  "LITIGATION",
  "CORPORATE_AND_COMMERCIAL",
  "IMMIGRATION",
  "EMPLOYMENT",
  "PERSONAL_INJURY",
  "CRIMINAL_DEFENCE",
  "OTHER"
]);
var MatterTypeLabels = {
  CONVEYANCING_RESIDENTIAL: "Conveyancing (Residential)",
  CONVEYANCING_COMMERCIAL: "Conveyancing (Commercial)",
  PROBATE_AND_WILLS: "Probate and Wills",
  FAMILY_LAW: "Family Law",
  LITIGATION: "Litigation",
  CORPORATE_AND_COMMERCIAL: "Corporate and Commercial",
  IMMIGRATION: "Immigration",
  EMPLOYMENT: "Employment",
  PERSONAL_INJURY: "Personal Injury",
  CRIMINAL_DEFENCE: "Criminal Defence",
  OTHER: "Other"
};
var ClientCompanySchema = z15.object({
  id: z15.string().uuid().optional(),
  // Optional for new companies
  companyName: z15.string().min(1, "Company name is required"),
  dateOfIncorporation: z15.coerce.date().nullable().optional(),
  addressLine1: z15.string().optional(),
  addressLine2: z15.string().optional(),
  townCity: z15.string().optional(),
  county: z15.string().optional(),
  postcode: z15.string().optional(),
  country: z15.string().default("GB")
});
var DraftClientCompanySchema = z15.object({
  id: z15.string().uuid().optional(),
  companyName: z15.string().optional(),
  // No min length for drafts
  dateOfIncorporation: z15.coerce.date().nullable().optional(),
  addressLine1: z15.string().optional(),
  addressLine2: z15.string().optional(),
  townCity: z15.string().optional(),
  county: z15.string().optional(),
  postcode: z15.string().optional(),
  country: z15.string().optional()
});
var CreateClientDetailsSchema = z15.object({
  // Client Reference & Type
  clientRef: z15.string().min(1, "Client reference is required"),
  clientType: ClientTypeSchema.default("NEW"),
  entityType: EntityTypeSchema.optional().nullable().or(z15.literal("").transform(() => null)),
  // Handle empty string from form select
  matterType: MatterTypeSchema.optional().nullable().or(z15.literal("").transform(() => null)),
  // Handle empty string from form select
  // Legal matter flag (stored on RiskAssessment, controls SRA form visibility)
  isLegalMatter: z15.boolean().default(false),
  // Personal Information
  titles: z15.array(ClientTitleSchema).max(10, "Maximum of 10 titles allowed").optional().nullable(),
  forename: z15.string().min(1, "Forename is required"),
  middleName: z15.string().optional().nullable(),
  surname: z15.string().min(1, "Surname is required"),
  dateOfBirth: z15.coerce.date().refine((date) => date < /* @__PURE__ */ new Date(), {
    message: "Date of birth must be in the past"
  }),
  nationality: z15.string().min(2, "Nationality is required"),
  countryOfResidence: z15.string().min(2, "Country of residence is required"),
  // Address
  addressLine1: z15.string().optional().nullable(),
  addressLine2: z15.string().optional().nullable(),
  townCity: z15.string().min(1, "Town/City is required").optional().nullable(),
  county: z15.string().optional().nullable(),
  postcode: z15.string().optional().nullable(),
  // Occupation
  occupation: z15.string().min(1, "Occupation/Source of income is required"),
  // Companies (optional)
  companies: z15.array(ClientCompanySchema).optional(),
  // Consent (required for submission)
  consentGiven: z15.boolean().refine((val) => val === true, {
    message: "You must confirm consent to proceed"
  })
});
var SaveDraftClientDetailsSchema = z15.object({
  clientRef: z15.string().optional(),
  clientType: ClientTypeSchema.optional(),
  entityType: EntityTypeSchema.optional().nullable().or(z15.literal("").transform(() => null)),
  matterType: MatterTypeSchema.optional().nullable().or(z15.literal("").transform(() => null)),
  isLegalMatter: z15.boolean().optional(),
  titles: z15.array(ClientTitleSchema).max(10).optional().nullable(),
  forename: z15.string().optional(),
  middleName: z15.string().optional().nullable(),
  surname: z15.string().optional(),
  dateOfBirth: z15.coerce.date().optional().nullable(),
  nationality: z15.string().optional(),
  countryOfResidence: z15.string().optional(),
  addressLine1: z15.string().optional().nullable(),
  addressLine2: z15.string().optional().nullable(),
  townCity: z15.string().optional().nullable(),
  county: z15.string().optional().nullable(),
  postcode: z15.string().optional().nullable(),
  occupation: z15.string().optional(),
  companies: z15.array(DraftClientCompanySchema).optional(),
  consentGiven: z15.boolean().optional()
});
var ClientDetailsResponseSchema = z15.object({
  id: z15.string().uuid(),
  clientRef: z15.string(),
  clientType: ClientTypeSchema,
  entityType: EntityTypeSchema.nullable(),
  matterType: MatterTypeSchema.nullable(),
  titles: z15.array(ClientTitleSchema).nullable(),
  forename: z15.string(),
  middleName: z15.string().nullable(),
  surname: z15.string(),
  dateOfBirth: z15.coerce.date(),
  nationality: z15.string(),
  countryOfResidence: z15.string(),
  addressLine1: z15.string().nullable(),
  addressLine2: z15.string().nullable(),
  townCity: z15.string(),
  county: z15.string().nullable(),
  postcode: z15.string().nullable(),
  occupation: z15.string(),
  consentGiven: z15.boolean(),
  consentTimestamp: z15.coerce.date().nullable(),
  riskAssessmentId: z15.string().uuid(),
  companies: z15.array(
    ClientCompanySchema.extend({
      id: z15.string().uuid(),
      clientDetailsId: z15.string().uuid(),
      createdAt: z15.coerce.date(),
      updatedAt: z15.coerce.date()
    })
  ).optional(),
  createdAt: z15.coerce.date(),
  updatedAt: z15.coerce.date()
});
var StartAssessmentResponseSchema = z15.object({
  id: z15.string().uuid(),
  optimalKycRef: z15.string(),
  status: z15.literal("IN_PROGRESS"),
  createdAt: z15.coerce.date()
});

// src/email-verification.schema.ts
import { z as z16 } from "zod";
var VerifyEmailSchema = z16.object({
  token: z16.string().min(1, "Verification token is required").describe("Email verification token")
});

// src/env.schema.ts
import { z as z17 } from "zod";
var EnvironmentSchema = z17.enum(["local", "development", "staging", "production", "test"]);
var WorkerModeSchema = z17.enum(["api", "worker", "both"]);
var WorkerTypeSchema = z17.enum(["browser", "compute", "all"]);
var EnvSchema = z17.object({
  NODE_ENV: EnvironmentSchema.default("local").describe("Application environment"),
  PORT: z17.string().regex(/^\d+$/, "PORT must be a number").transform((val) => parseInt(val, 10)).pipe(z17.number().int().min(1).max(65535)).default("3000").describe("Server port (1-65535)"),
  DATABASE_URL: z17.string().min(1, "DATABASE_URL is required").describe("PostgreSQL connection string"),
  JWT_ACCESS_SECRET: z17.string().min(1, "JWT_ACCESS_SECRET is required").describe("Secret for access tokens"),
  JWT_REFRESH_SECRET: z17.string().min(1, "JWT_REFRESH_SECRET is required").describe("Secret for refresh tokens"),
  COOKIE_SECRET: z17.string().min(1, "COOKIE_SECRET is required").describe("Secret for cookie signing"),
  RATE_LIMIT_MAX: z17.string().regex(/^\d+$/, "RATE_LIMIT_MAX must be a number").transform((val) => parseInt(val, 10)).pipe(z17.number().int().min(1)).default("100").describe("Maximum requests per rate limit window"),
  OTEL_EXPORTER_OTLP_ENDPOINT: z17.string().optional().describe("OpenTelemetry collector endpoint (optional)"),
  // Sentry error monitoring (optional — when SENTRY_DSN is unset, Sentry is
  // disabled and the SDK becomes a no-op).
  SENTRY_DSN: z17.string().optional().describe("Sentry DSN for error reporting. When unset, Sentry is disabled."),
  SENTRY_ENVIRONMENT: z17.string().optional().describe("Sentry environment tag (defaults to NODE_ENV when unset)."),
  SENTRY_TRACES_SAMPLE_RATE: z17.string().regex(/^(0(\.\d+)?|1(\.0+)?)$/, "SENTRY_TRACES_SAMPLE_RATE must be between 0 and 1").transform((val) => parseFloat(val)).optional().describe("Sentry performance tracing sample rate (0\u20131). Defaults to 0 (disabled)."),
  SENTRY_DEBUG: z17.enum(["true", "false"]).optional().describe("Enable verbose Sentry SDK logging to confirm events are sent (default false)."),
  PROM_AUTH_USER: z17.string().optional().describe("Prometheus basic auth username (optional)"),
  PROM_AUTH_PASS: z17.string().optional().describe("Prometheus basic auth password (optional)"),
  // Mailtrap SMTP Configuration
  SMTP_HOST: z17.string().default("sandbox.smtp.mailtrap.io").describe("SMTP server host"),
  SMTP_PORT: z17.string().regex(/^\d+$/, "SMTP_PORT must be a number").transform((val) => parseInt(val, 10)).pipe(z17.number().int().min(1).max(65535)).default("2525").describe("SMTP server port"),
  SMTP_USER: z17.string().optional().describe("SMTP username (Mailtrap credentials)"),
  SMTP_PASS: z17.string().optional().describe("SMTP password (Mailtrap credentials)"),
  SMTP_FROM_EMAIL: z17.string().email().default("noreply@okyc.ai").describe("From email address"),
  SMTP_FROM_NAME: z17.string().default("Optimal KYC").describe("From name"),
  // Frontend URL for password reset links
  FRONTEND_URL: z17.string().url().default("http://localhost:5173").describe("Frontend application URL"),
  // Evidence Storage Configuration
  STORAGE_TYPE: z17.enum(["local", "s3"]).default("local").describe("Storage provider type: local (filesystem) or s3"),
  EVIDENCE_STORAGE_PATH: z17.string().optional().describe("Local storage path for evidence files (only used when STORAGE_TYPE=local)"),
  S3_EVIDENCE_BUCKET: z17.string().optional().describe("S3 bucket name for evidence storage (required when STORAGE_TYPE=s3)"),
  S3_EVIDENCE_PREFIX: z17.string().default("evidence").describe("S3 key prefix for evidence files"),
  AWS_REGION: z17.string().default("eu-west-2").describe("AWS region for S3 bucket"),
  AWS_BEDROCK_MODEL_ID: z17.string().default("openai.gpt-oss-120b-1:0").refine(
    (val) => !val.toLowerCase().includes("anthropic") && !val.toLowerCase().includes("claude"),
    { message: "Anthropic/Claude models are not allowed. Use openai.gpt-oss-120b-1:0" }
  ).describe("AWS Bedrock model ID (must be gpt-oss-120b)"),
  // Agent API Keys
  COMPANIES_HOUSE_API_KEY: z17.string().optional().describe("Companies House API key for officer/company lookups"),
  SEARCH_PROVIDER: z17.enum(["brave", "google"]).optional().default("brave").describe("Search provider for adverse media and PEP screening (brave or google)"),
  BRAVE_SEARCH_API_KEY: z17.string().optional().describe("Brave Search API key for adverse media and PEP screening"),
  GOOGLE_API_KEY: z17.string().optional().describe("Google Custom Search API key"),
  GOOGLE_SEARCH_ENGINE_ID: z17.string().optional().describe("Google Custom Search Engine ID (cx parameter)"),
  BRAVE_GOGGLES_ENABLED: z17.string().optional().default("true").describe("Enable Brave Search Goggles to boost trusted media sources (true/false)"),
  BRAVE_GOGGLES_URL: z17.string().optional().describe(
    "URL to hosted Goggles file (e.g. https://your-domain.com/api/goggles/media-sources). If not set, rules are sent inline."
  ),
  // Queue Configuration
  REDIS_URL: z17.string().optional().describe("Redis connection URL for job queue (e.g., redis://localhost:6379)"),
  WORKER_MODE: WorkerModeSchema.default("api").describe(
    "Instance mode: api (HTTP only), worker (queue only), both (legacy combined mode)"
  ),
  WORKER_TYPE: WorkerTypeSchema.default("all").describe(
    "Queue type to process: browser (Chromium jobs), compute (AI jobs), all (development)"
  ),
  DISABLE_NODE_WORKER: z17.enum(["true", "false"]).default("false").transform((val) => val === "true").describe("Disable in-process queue consumer (rely on ECS workers)"),
  // Parity Runner Configuration
  PARITY_RUNNER_ENABLED: z17.enum(["true", "false"]).default("false").transform((val) => val === "true").describe("Enable parity runner evaluation system (admin only)"),
  // Public Registration
  REGISTRATION_ENABLED: z17.enum(["true", "false"]).default("false").transform((val) => val === "true").describe(
    "Enable public user registration (POST /auth/register). Defaults to false to prevent unauthorised account creation on live environments."
  ),
  // GDPR/Support Contact Configuration
  GDPR_CONTACT_EMAIL: z17.string().email().default("dpo@okyc.ai").describe("Data Protection Officer contact email"),
  SUPPORT_EMAIL: z17.string().email().default("support@okyc.ai").describe("Support team contact email"),
  // Stripe Payment Configuration
  STRIPE_SECRET_KEY: z17.string().optional().describe("Stripe secret API key (sk_test_... or sk_live_...)"),
  STRIPE_WEBHOOK_SECRET: z17.string().optional().describe("Stripe webhook signing secret (whsec_...)"),
  STRIPE_PUBLISHABLE_KEY: z17.string().optional().describe("Stripe publishable key for frontend (pk_test_... or pk_live_...)"),
  // CORS Configuration
  CORS_ALLOWED_ORIGINS: z17.string().optional().describe(
    "Comma-separated list of allowed CORS origins (e.g., https://app.okyc.ai,https://staging.okyc.ai). If not set, CORS is disabled."
  ),
  // Document Upload Configuration
  UPLOAD_TOKEN_EXPIRY_DAYS: z17.string().regex(/^\d+$/, "UPLOAD_TOKEN_EXPIRY_DAYS must be a number").transform((val) => parseInt(val, 10)).pipe(z17.number().int().min(1).max(30)).optional().describe("Upload token expiry in days (default: 7, max: 30)"),
  UPLOAD_DOCUMENT_EXPIRY_DAYS: z17.string().regex(/^\d+$/, "UPLOAD_DOCUMENT_EXPIRY_DAYS must be a number").transform((val) => parseInt(val, 10)).pipe(z17.number().int().min(1).max(90)).optional().describe("Document retention period in days after upload (default: 10, max: 90)"),
  // Risk Scoring Configuration
  MAX_RISK_SCORE: z17.string().regex(/^\d+$/, "MAX_RISK_SCORE must be a number").transform((val) => parseInt(val, 10)).pipe(z17.number().int().min(0).max(100)).optional().describe("Maximum risk score cap (0-100). Defaults to 99.")
}).refine((data) => data.STORAGE_TYPE !== "s3" || !!data.S3_EVIDENCE_BUCKET, {
  message: "S3_EVIDENCE_BUCKET is required when STORAGE_TYPE is s3"
}).refine(
  (data) => {
    if (data.WORKER_MODE !== "api" && !data.REDIS_URL) {
      return false;
    }
    return true;
  },
  {
    message: "REDIS_URL is required when WORKER_MODE is worker or both"
  }
).refine(
  (data) => {
    if (data.STRIPE_SECRET_KEY && !data.STRIPE_WEBHOOK_SECRET) {
      return false;
    }
    return true;
  },
  {
    message: "STRIPE_WEBHOOK_SECRET is required when STRIPE_SECRET_KEY is set"
  }
).refine(
  (data) => {
    if (data.NODE_ENV !== "production") return true;
    try {
      const { hostname } = new URL(data.FRONTEND_URL);
      return hostname !== "localhost" && hostname !== "127.0.0.1";
    } catch {
      return false;
    }
  },
  {
    message: "FRONTEND_URL must be set to a non-localhost URL in production (used in report-ready email links)"
  }
);

// src/feature-flag.schema.ts
import { z as z18 } from "zod";
var JsonValueSchema = z18.lazy(
  () => z18.union([
    z18.string(),
    z18.number(),
    z18.boolean(),
    z18.record(JsonValueSchema),
    z18.array(JsonValueSchema)
  ])
);
var CreateFeatureFlagSchema = z18.object({
  key: z18.string().min(1, "Key is required").regex(/^[a-z0-9_]+$/, "Key must contain only lowercase letters, numbers, and underscores").describe("Unique feature flag key (snake_case)"),
  enabled: z18.boolean().default(false).describe("Whether the feature flag is enabled"),
  description: z18.string().optional().describe("Human-readable description of the feature flag"),
  strategies: JsonValueSchema.optional().describe("Targeting strategies (JSON object)")
});
var UpdateFeatureFlagSchema = z18.object({
  enabled: z18.boolean().optional().describe("Whether the feature flag is enabled"),
  description: z18.string().optional().describe("Human-readable description of the feature flag"),
  strategies: JsonValueSchema.optional().describe("Targeting strategies (JSON object)")
});
var FeatureFlagSchema = z18.object({
  key: z18.string(),
  enabled: z18.boolean(),
  description: z18.string().nullable(),
  strategies: z18.union([JsonValueSchema, z18.null()]),
  createdAt: z18.date(),
  updatedAt: z18.date()
});

// src/password-reset.schema.ts
import { z as z19 } from "zod";
var ForgotPasswordSchema = z19.object({
  email: z19.string().email("Invalid email address").describe("User email address")
});
var ResetPasswordSchema = z19.object({
  token: z19.string().min(1, "Reset token is required").describe("Password reset token"),
  password: z19.string().min(8, "Password must be at least 8 characters").describe("New password"),
  confirmPassword: z19.string().min(1, "Please confirm your password").describe("Password confirmation")
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

// src/payment.schema.ts
import { z as z20 } from "zod";
var MIN_PAYMENT_AMOUNT_PENCE = 500;
var MAX_PAYMENT_AMOUNT_PENCE = 1e6;
var CreatePaymentIntentSchema = z20.object({
  amount: z20.number().int("Amount must be a whole number (pence)").min(MIN_PAYMENT_AMOUNT_PENCE, `Minimum amount is ${MIN_PAYMENT_AMOUNT_PENCE} pence (\xA35.00)`).max(
    MAX_PAYMENT_AMOUNT_PENCE,
    `Maximum amount is ${MAX_PAYMENT_AMOUNT_PENCE} pence (\xA310,000.00)`
  ).describe("Amount in pence (e.g., 500 = \xA35.00)")
});
var PaymentIntentResponseSchema = z20.object({
  clientSecret: z20.string().describe("Stripe client secret for confirming payment"),
  paymentIntentId: z20.string().describe("Stripe PaymentIntent ID"),
  amount: z20.number().describe("Amount in pence")
});
var PaymentStatusSchema = z20.enum([
  "PENDING",
  "PROCESSING",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED"
]);

// src/project.schema.ts
import { z as z21 } from "zod";
var ProjectSchema = z21.object({
  id: z21.string().uuid().describe("Project unique identifier"),
  name: z21.string().min(3, "Name must be at least 3 characters").describe("Project name"),
  description: z21.string().nullable().optional().describe("Project description (optional)"),
  userId: z21.string().uuid().describe("Owner user ID"),
  createdAt: z21.date().describe("Creation timestamp"),
  updatedAt: z21.date().describe("Last update timestamp")
});
var CreateProjectSchema = z21.object({
  name: z21.string().min(1, "Project name is required").max(100, "Project name must not exceed 100 characters").describe("The name of the project"),
  description: z21.string().max(500, "Description must not exceed 500 characters").optional().describe("A brief description of the project")
});
var UpdateProjectSchema = z21.object({
  name: z21.string().min(1, "Project name is required").max(100, "Project name must not exceed 100 characters").optional().describe("The name of the project"),
  description: z21.string().max(500, "Description must not exceed 500 characters").optional().describe("A brief description of the project")
});

// src/risk-assessment.schema.ts
import { z as z22 } from "zod";
var RiskLevelSchema = z22.enum(["LOW", "MEDIUM", "HIGH"]);
var DocumentRequestSchema = z22.object({
  id: z22.string(),
  documentType: z22.string(),
  otherDescription: z22.string().optional(),
  dateRequested: z22.coerce.date(),
  received: z22.boolean(),
  downloadUrl: z22.string().optional()
});
var AssessmentStatusSchema = z22.enum(["IN_PROGRESS", "COMPLETED", "DELETED"]);
var ScreeningJobStatusSchema = z22.enum([
  "NOT_STARTED",
  // No screening has been initiated
  "FAST_CHECKS_DONE",
  // Quick checks complete, adverse media queued
  "PROCESSING",
  // Adverse media check in progress
  "COMPLETED",
  // All checks complete
  "FAILED"
  // Processing failed after retries
]);
var ScreeningCheckIdSchema = z22.enum([
  "UK",
  // HM Treasury Sanctions List
  "US",
  // US Consolidated Sanctions List (OFAC)
  "UN",
  // UN Sanctions List
  "EU",
  // EU Sanctions List
  "PEP",
  // Politically Exposed Persons
  "ADVERSE_MEDIA",
  // Adverse Media screening
  "COMPANIES_HOUSE",
  // Companies House officer search
  "CPI",
  // Corruption Perceptions Index
  "FATF"
  // Financial Action Task Force jurisdictions
]);
var ALL_SCREENING_CHECKS = [
  "UK",
  "US",
  "UN",
  "EU",
  "PEP",
  "ADVERSE_MEDIA",
  "COMPANIES_HOUSE",
  "CPI",
  "FATF"
];
var REQUIRED_CHECKS = ["UK"];
var PaymentFlowStepSchema = z22.enum(["REQUEST_DOCS", "MATCH_REPORT", "EDD"]);
var RiskAssessmentSchema = z22.object({
  id: z22.string(),
  optimalKycRef: z22.string(),
  // e.g., "OK10"
  clientRef: z22.string(),
  // e.g., "SRA013"
  clientName: z22.string(),
  clientEmail: z22.string().email().optional(),
  riskLevel: RiskLevelSchema,
  status: AssessmentStatusSchema,
  screeningJobStatus: ScreeningJobStatusSchema.optional(),
  // Queue processing status
  paymentFlowStep: PaymentFlowStepSchema.nullable().optional(),
  // Current step in payment flow for resumption
  isLegalMatter: z22.boolean().default(false),
  // Whether this is a legal matter (for SRA form generation)
  documentRequestLinkGenerated: z22.boolean(),
  documentRequests: z22.array(DocumentRequestSchema).optional(),
  hasNewDocuments: z22.boolean().default(false),
  reportDownloadWindowDays: z22.number().nullable(),
  // null = expired
  reportExpired: z22.boolean().default(false),
  needsRefresh: z22.boolean().default(false),
  // Whether assessment needs refresh based on user's due diligence frequency
  // Refresh policy (Feature: retention-and-refresh-policy)
  refreshCount: z22.number().int().default(0),
  refreshesUsed: z22.number().int().default(0),
  refreshesRemaining: z22.number().int().default(0),
  canRefresh: z22.boolean().default(false),
  // PII purge marker — non-null means the assessment was wiped by the daily
  // retention cron; client data is gone but the audit stub (ref, checks,
  // timestamps) is retained.
  purgedAt: z22.coerce.date().nullable().optional(),
  reportData: z22.record(z22.unknown()).nullable().optional(),
  // FinalResult JSON from screening
  formattedReport: z22.string().nullable().optional(),
  // Human-readable screening report
  enabledChecks: z22.array(ScreeningCheckIdSchema).optional(),
  // Which checks were run
  dateReportGenerated: z22.coerce.date().nullable(),
  dateDeleted: z22.coerce.date().nullable(),
  createdAt: z22.coerce.date(),
  updatedAt: z22.coerce.date()
});
var RiskAssessmentListResponseSchema = z22.object({
  data: z22.array(RiskAssessmentSchema),
  total: z22.number(),
  page: z22.number(),
  pageSize: z22.number()
});
var RiskAssessmentSortColumnSchema = z22.enum([
  "clientRef",
  "riskLevel",
  "dateReportGenerated",
  "createdAt"
]);
var RiskAssessmentFiltersSchema = z22.object({
  search: z22.string().optional(),
  riskLevels: z22.union([RiskLevelSchema, z22.array(RiskLevelSchema)]).optional().transform((val) => val ? Array.isArray(val) ? val : [val] : void 0),
  reportDownloadWindow: z22.coerce.number().optional(),
  dateFrom: z22.coerce.date().optional(),
  dateTo: z22.coerce.date().optional(),
  status: AssessmentStatusSchema.optional(),
  page: z22.coerce.number().default(1),
  pageSize: z22.coerce.number().default(20),
  sortBy: RiskAssessmentSortColumnSchema.optional(),
  sortOrder: z22.enum(["asc", "desc"]).optional()
});
var GenerateDocumentLinkSchema = z22.object({
  assessmentId: z22.string(),
  clientEmail: z22.string().email(),
  documentTypes: z22.array(z22.string()).min(1),
  otherDescription: z22.string().optional()
});
var DocumentTypeSchema = z22.enum([
  "IDENTITY_DOCUMENT",
  "PROOF_OF_ADDRESS",
  "PROOF_OF_INCOME",
  "BANK_STATEMENT",
  "TAX_RETURN",
  "OTHER"
]);
var DocumentTypeLabels = {
  IDENTITY_DOCUMENT: "Identity Document",
  PROOF_OF_ADDRESS: "Proof of Address",
  PROOF_OF_INCOME: "Proof of Income",
  BANK_STATEMENT: "Bank Statement",
  TAX_RETURN: "Tax Return",
  OTHER: "Other"
};
var CreateRiskAssessmentSchema = z22.object({
  clientRef: z22.string().min(1),
  clientName: z22.string().min(1),
  clientEmail: z22.string().email().optional(),
  riskLevel: RiskLevelSchema,
  reportData: z22.record(z22.unknown()).optional(),
  // FinalResult JSON
  formattedReport: z22.string().optional()
});
var UpdateRiskAssessmentSchema = z22.object({
  clientRef: z22.string().min(1).optional(),
  clientName: z22.string().min(1).optional(),
  clientEmail: z22.string().email().optional(),
  riskLevel: RiskLevelSchema.optional(),
  status: AssessmentStatusSchema.optional()
});
var BulkOperationSchema = z22.object({
  ids: z22.array(z22.string().uuid()).min(1)
});

// src/roles.ts
var SUPERADMIN_ROLE = "superadmin";
var ADMIN_PERMISSION = "admin:users";

// src/service-keys.ts
import { z as z23 } from "zod";
var SERVICE_KEYS = [
  "pep_sanctions_screening",
  "ai_confidence_report",
  "match_risk_report",
  "documentation_request"
];
var ServiceKeyEnum = z23.enum(SERVICE_KEYS);
var TIER_KEYS = ["tier_basic", "tier_standard", "tier_enhanced"];
var TierKeyEnum = z23.enum(TIER_KEYS);
var BILLABLE_KEYS = [...SERVICE_KEYS, ...TIER_KEYS];
var BillableKeyEnum = z23.enum(BILLABLE_KEYS);
var PAID_SERVICE_PROPERTIES = [
  "initialScreening",
  "matchReport",
  "eddReport",
  "docRequest"
];
var SERVICE_KEY_PAID_PROPERTY = {
  pep_sanctions_screening: "initialScreening",
  ai_confidence_report: "eddReport",
  match_risk_report: "matchReport",
  documentation_request: "docRequest"
};
var TIER_INCLUDED_SERVICES = {
  tier_basic: ["pep_sanctions_screening"],
  tier_standard: ["pep_sanctions_screening", "match_risk_report"],
  tier_enhanced: ["pep_sanctions_screening", "match_risk_report", "ai_confidence_report"]
};

// src/settings.schema.ts
import { z as z24 } from "zod";
var DueDiligenceFrequencyEnum = z24.enum(["MONTHLY", "QUARTERLY", "ANNUALLY", "NEVER"]);
var UpdateProfileSchema = z24.object({
  title: z24.string().min(1, "Title is required").max(20).optional(),
  forename: z24.string().min(1, "Forename is required").max(100).optional(),
  middleName: z24.string().max(100).nullable().optional(),
  surname: z24.string().min(1, "Surname is required").max(100).optional(),
  jobTitle: z24.string().min(1, "Job title is required").max(100).optional(),
  addressLine1: z24.string().min(1, "Address line 1 is required").max(200).optional(),
  addressLine2: z24.string().max(200).nullable().optional(),
  townCity: z24.string().min(1, "Town/City is required").max(100).optional(),
  county: z24.string().max(100).nullable().optional(),
  postcode: z24.string().min(1, "Postcode is required").max(20).optional(),
  country: z24.string().min(2, "Country is required").max(2).optional()
});
var UpdateCompanySchema = z24.object({
  name: z24.string().min(1, "Company name is required").max(200).optional(),
  industry: IndustryEnum.optional(),
  website: companyWebsite().nullable().optional(),
  // Accepts '' (treated as 'no change' by callers) so a user clearing the
  // field doesn't 400 on the regex before `.optional()` fires. The frontend
  // submit handler should map '' → undefined; this is defence-in-depth.
  companyNumber: z24.union([
    z24.literal(""),
    z24.string().trim().regex(/^[A-Za-z0-9]{8}$/, "Enter a valid 8-character Companies House number").transform((value) => value.toUpperCase())
  ]).optional(),
  addressLine1: z24.string().min(1, "Address line 1 is required").max(200).optional(),
  addressLine2: z24.string().max(200).nullable().optional(),
  townCity: z24.string().min(1, "Town/City is required").max(100).optional(),
  county: z24.string().max(100).nullable().optional(),
  postcode: z24.string().min(1, "Postcode is required").max(20).optional(),
  country: z24.string().length(2, "Country must be a 2-letter ISO code").optional()
});
var UpdateDueDiligenceSchema = z24.object({
  frequency: DueDiligenceFrequencyEnum
});
var DeleteAccountSchema = z24.object({
  confirmEmail: z24.string().email("Please enter your email"),
  password: z24.string().min(1, "Password is required")
});
var SettingsFormSchema = z24.object({
  // User Info
  title: z24.string().min(1, "Title is required"),
  forename: z24.string().min(1, "Forename is required"),
  middleName: z24.string().optional(),
  surname: z24.string().min(1, "Surname is required"),
  jobTitle: z24.string().max(100).optional().or(z24.literal("")),
  // User's Personal Address
  personalAddressLine1: z24.string().max(200).optional().or(z24.literal("")),
  personalAddressLine2: z24.string().max(200).optional(),
  personalTownCity: z24.string().max(100).optional().or(z24.literal("")),
  personalCounty: z24.string().optional(),
  personalPostcode: z24.string().max(20).optional().or(z24.literal("")),
  personalCountry: z24.string().length(2, "Personal country must be a 2-letter ISO code").optional().or(z24.literal("")),
  // Due Diligence
  dueDiligenceAlertFrequency: DueDiligenceFrequencyEnum,
  // Company
  companyName: z24.string().min(1, "Company name is required"),
  industry: IndustryEnum,
  companyNumber: z24.string().trim().regex(/^[A-Za-z0-9]{8}$/, "Enter a valid 8-character Companies House number").optional().or(z24.literal("")),
  companyWebsite: companyWebsite().optional(),
  // Company Registered Address (Companies House)
  addressLine1: z24.string().min(1, "Address line 1 is required"),
  addressLine2: z24.string().optional(),
  townCity: z24.string().min(1, "Town/City is required"),
  county: z24.string().optional(),
  postcode: z24.string().min(1, "Postcode is required"),
  country: z24.string().length(2, "Country must be a 2-letter ISO code")
});
var PricingItemSchema = z24.object({
  key: z24.string(),
  name: z24.string(),
  description: z24.string().nullable(),
  priceGBP: z24.string()
  // Decimal as string for precision
});
var ProfileResponseSchema = z24.object({
  user: z24.object({
    id: z24.string(),
    email: z24.string(),
    title: z24.string().nullable(),
    forename: z24.string().nullable(),
    middleName: z24.string().nullable(),
    surname: z24.string().nullable(),
    jobTitle: z24.string().nullable(),
    addressLine1: z24.string().nullable(),
    addressLine2: z24.string().nullable(),
    townCity: z24.string().nullable(),
    county: z24.string().nullable(),
    postcode: z24.string().nullable(),
    country: z24.string(),
    dueDiligenceAlertFrequency: DueDiligenceFrequencyEnum,
    balance: z24.string()
    // Decimal as string
  }),
  company: z24.object({
    id: z24.string(),
    name: z24.string(),
    industry: IndustryEnum,
    website: z24.string().nullable(),
    companyNumber: z24.string().nullable(),
    addressLine1: z24.string().nullable(),
    addressLine2: z24.string().nullable(),
    townCity: z24.string().nullable(),
    county: z24.string().nullable(),
    postcode: z24.string().nullable(),
    country: z24.string()
  }).nullable()
});

// src/user.schema.ts
import { z as z25 } from "zod";
var RoleSchema = z25.object({
  id: z25.string().uuid(),
  name: z25.string().min(1),
  description: z25.string().nullable(),
  createdAt: z25.date(),
  updatedAt: z25.date()
});
var CompanySchema = z25.object({
  id: z25.string().uuid(),
  name: z25.string(),
  industry: IndustryEnum,
  website: z25.string().nullable(),
  addressLine1: z25.string().nullable(),
  addressLine2: z25.string().nullable(),
  townCity: z25.string().nullable(),
  county: z25.string().nullable(),
  postcode: z25.string().nullable(),
  country: z25.string(),
  createdAt: z25.date(),
  updatedAt: z25.date()
});
var PermissionSchema = z25.object({
  id: z25.string().uuid(),
  name: z25.string().min(1),
  description: z25.string().nullable(),
  createdAt: z25.date(),
  updatedAt: z25.date()
});
var UserSchema = z25.object({
  id: z25.string().uuid(),
  email: z25.string().email(),
  passwordHash: z25.string(),
  roleId: z25.string().uuid(),
  role: RoleSchema.optional(),
  failedLoginAttempts: z25.number().int().min(0),
  lockedUntil: z25.date().nullable(),
  // Personal Information (from account setup)
  title: z25.string().nullable(),
  forename: z25.string().nullable(),
  middleName: z25.string().nullable(),
  surname: z25.string().nullable(),
  accountSetupComplete: z25.boolean(),
  accountStatus: z25.enum(ACCOUNT_STATUSES),
  // Email verification
  emailVerified: z25.boolean(),
  emailVerifiedAt: z25.date().nullable(),
  // Company relationship
  companyId: z25.string().uuid().nullable(),
  createdAt: z25.date(),
  updatedAt: z25.date()
});
var PublicUserSchema = UserSchema.omit({
  passwordHash: true,
  failedLoginAttempts: true,
  lockedUntil: true
}).extend({
  role: z25.object({
    id: z25.string().uuid(),
    name: z25.string(),
    description: z25.string().nullable()
  }).optional(),
  company: CompanySchema.nullable().optional(),
  /**
   * The company's shared balance, in pounds.
   *
   * Pooled across every user of the firm rather than held per user, so two
   * colleagues see the same figure and spending by one moves it for both. Kept
   * on the user payload because that is where the client already reads it, and
   * because a user only ever has one firm's pool to draw on.
   */
  balance: z25.union([z25.string(), z25.number()]).transform((val) => Number(val)),
  // Flat list of the role's permission names, so the client can make the same
  // authorisation decisions as the API rather than gating on role name.
  //
  // Defaulted for tolerance of responses issued before this field existed.
  // Note that superadmin carries an empty array by design and relies on a
  // role-name bypass — never treat an empty array as "no access".
  permissions: z25.array(z25.string()).default([])
});
var CreateUserSchema = z25.object({
  email: z25.string().email("Invalid email address").describe("User email address"),
  password: z25.string().min(8, "Password must be at least 8 characters").describe("User password")
});
export {
  ACCOUNT_REFUSAL_CODES,
  ACCOUNT_STATUSES,
  ACTIVITY_SORT_COLUMNS,
  ADMIN_PERMISSION,
  ADMIN_USER_SORT_COLUMNS,
  ADMIN_USER_STATES,
  ALL_SCREENING_CHECKS,
  ASSESSMENT_RISK_LEVELS,
  ASSESSMENT_SORT_COLUMNS,
  ASSESSMENT_STATUSES,
  AccountSetupSchema,
  ActivityLogItemSchema,
  ActivityLogPageSchema,
  ActivityLogQuerySchema,
  AdminAssessmentDetailSchema,
  AdminAssessmentListItemSchema,
  AdminAssessmentPageSchema,
  AdminAssessmentQuerySchema,
  AdminAssessmentReportStateSchema,
  AdminAssessmentRiskLevelSchema,
  AdminAssessmentStatusSchema,
  AdminOverviewSchema,
  AdminTransactionPageSchema,
  AdminTransactionQuerySchema,
  AdminTransactionSchema,
  AdminUserDetailSchema,
  AdminUserListItemSchema,
  AdminUserListPageSchema,
  AdminUserListQuerySchema,
  ApproveUserSchema,
  AssessmentStatusSchema,
  BILLABLE_KEYS,
  BillableKeyEnum,
  BulkOperationSchema,
  COMPANY_OUTCOME_KINDS,
  CheckDepthTierSchema,
  ClientCompanySchema,
  ClientDetailsResponseSchema,
  ClientTitleLabels,
  ClientTitleSchema,
  ClientTypeSchema,
  CompanyRiskReportSchema,
  CompanyRiskRowSchema,
  CompanySchema,
  CorrectCompanyClaimSchema,
  CreateClientDetailsSchema,
  CreateFeatureFlagSchema,
  CreatePaymentIntentSchema,
  CreateProjectSchema,
  CreateRiskAssessmentSchema,
  CreateUserSchema,
  CreditCompanyResultSchema,
  CreditCompanySchema,
  CreditableCompanySchema,
  DeleteAccountSchema,
  DisableUserSchema,
  DocumentRequestSchema,
  DocumentTypeLabels,
  DocumentTypeSchema,
  DraftClientCompanySchema,
  DueDiligenceFrequencyEnum,
  EntityTypeSchema,
  EnvSchema,
  EnvironmentSchema,
  FeatureFlagSchema,
  ForgotPasswordSchema,
  GenerateDocumentLinkSchema,
  InclusiveEndDateSchema,
  IndustryEnum,
  LoginSchema,
  MAX_CREDIT_PENCE,
  MAX_PAYMENT_AMOUNT_PENCE,
  MIN_PAYMENT_AMOUNT_PENCE,
  MatterTypeLabels,
  MatterTypeSchema,
  NO_COMPANY_ID,
  PAID_SERVICE_PROPERTIES,
  PaymentFlowStepSchema,
  PaymentIntentResponseSchema,
  PaymentStatusSchema,
  PermissionSchema,
  PricingItemSchema,
  ProfileResponseSchema,
  ProjectSchema,
  PublicUserSchema,
  REQUIRED_CHECKS,
  ReassignCompanySchema,
  RegisterSchema,
  RejectUserSchema,
  ResetPasswordSchema,
  RiskAssessmentFiltersSchema,
  RiskAssessmentListResponseSchema,
  RiskAssessmentSchema,
  RiskAssessmentSortColumnSchema,
  RiskLevelSchema,
  RoleSchema,
  SERVICE_KEYS,
  SERVICE_KEY_PAID_PROPERTY,
  SUPERADMIN_ROLE,
  SaveDraftClientDetailsSchema,
  ScreeningCheckIdSchema,
  ScreeningJobStatusSchema,
  ServiceKeyEnum,
  SettingsFormSchema,
  StartAssessmentResponseSchema,
  TIER_INCLUDED_SERVICES,
  TIER_KEYS,
  TRANSACTION_SORT_COLUMNS,
  TRANSACTION_SOURCES,
  TierKeyEnum,
  UpdateCompanySchema,
  UpdateDueDiligenceSchema,
  UpdateFeatureFlagSchema,
  UpdateProfileSchema,
  UpdateProjectSchema,
  UpdateRiskAssessmentSchema,
  UserSchema,
  VerifyEmailSchema,
  WorkerModeSchema,
  WorkerTypeSchema,
  companyWebsite,
  createUpdateSchema,
  csvList,
  omitSensitive
};
//# sourceMappingURL=index.js.map